<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { mdiAlert, mdiAlertBoxOutline, mdiArrowLeft, mdiEye, mdiGestureTap } from '@mdi/js'
import { UserService } from '@/services/user-service'
import { Settings } from '@/settings'
import { Notify } from '@/modules/util/notify'
import { Cache } from '@/modules/cache/main'

const route = useRoute()
const router = useRouter()

const active = ref(false)
const showDrawer = computed(() => active.value && !route.meta.hideNav)

const storage = ref(0)
const showStorageDialog = ref(false)

const toggle = () => {
	active.value = !active.value
}

const locationId = ref(parseInt(localStorage.getItem('LOCATION_ID')) || undefined)

const changeLocation = (id) => {
	localStorage.setItem('LOCATION_ID', id)
	locationId.value = id
	location.reload()
}

const logout = () => {
	UserService.logout()
	toggle()
	router.push({ name: 'login' })
}

const reload = async () => {
	try {
		const regs = await navigator.serviceWorker.getRegistrations()
		await Promise.all(regs.map(r => r.unregister()))
	} catch (e) { /* SW may not be supported */ }
	location.reload()
}

const updateStorage = async () => {
	const estimate = await navigator?.storage?.estimate()
	if (!estimate) {
		storage.value = 0
		return
	}
    
	storage.value = Math.round(estimate.usage * 100 / estimate.quota)
}

const clearCache = async () => {
	showStorageDialog.value = false
	router.push({ name: 'list-vehicles' })
	await Cache.clearAll()
	await updateStorage()
	Settings.clearProgress()
	Notify.success('Cache cleared successfully')
}

onMounted(() => {
	setInterval(updateStorage, 10000)
})
</script>

<template>
<v-app-bar
	v-show="!$route.meta.hideNav"
	:elevation="3"
	:color="Settings.APP_COLOR"
>
	<template #prepend>
		<v-app-bar-nav-icon
			v-if="route.meta.back"
			:icon="mdiArrowLeft"
			@click="router.push(route.meta.back(route))"
		/>
		<v-app-bar-nav-icon
			v-else
			color="white"
			@click="toggle"
		/>
	</template>
    
	<v-app-bar-title>Inspections</v-app-bar-title>

	<div v-if="storage >= 95">
		<v-btn
			:prepend-icon="mdiAlert"
			:append-icon="mdiGestureTap"
			color="warning"
			class="bg-white"
			@click="showStorageDialog = true"
		>
			{{ storage }}% disk used
		</v-btn>
	</div>

	<v-spacer />

	<div
		id="app-bar-actions-target"
		class="d-flex app-bar"
	/>
</v-app-bar>

<v-navigation-drawer
	v-model="showDrawer"
>
	<v-list-item subtitle="Menu" />
	<v-divider />
	<v-list-item>
		<v-select
			:model-value="locationId"
			:items="[
				{ id: -1, name: 'Auto' },
				...Settings.LOCATIONS,
			]"
			item-title="name"
			item-value="id"
			label="Location"
			variant="solo"
			density="comfortable"
			hide-details
			class="my-2 mx-1"
			@update:model-value="changeLocation"
		/>
	</v-list-item>
	<v-divider />
	<v-list-item
		link
		title="Reload"
		@click="reload"
	/>
	<v-list-item
		link
		title="Sign out"
		@click="logout"
	/>
	<v-divider />
	<v-list-item
		link
		title="Network"
		@click="() => {
			router.push({ name: 'request-view' })
			toggle()
		}"
	/>
	<v-list-item
		link
		title="Event log"
		@click="() => {
			router.push({ name: 'log-view' })
			toggle()
		}"
	/>
</v-navigation-drawer>

<v-dialog
	v-model="showStorageDialog"
	max-width="500px"
>
	<v-card title="Storage Usage">
		<v-card-text>
			<p>Disk usage is at {{ storage }}% of the available quota.</p><br>
			<p>
				You can clear all cached data to free up space. 
				Please ensure you have a good WiFi connection before doing so.
				Any in-progress uploads will be lost.
			</p><br>
			<p>Clear all cached data? (Any in-progress uploads will be lost)</p>
		</v-card-text>
		<v-card-actions
			class="d-flex flex-wrap pa-5"
			style="gap: 1rem;"
		>
			<v-btn 
				:prepend-icon="mdiAlertBoxOutline"
				color="error"
				class="flex-grow-1 w-100"
				variant="tonal"
				@click="clearCache"
			>
				Clear Storage
			</v-btn>
			<v-btn
				:prepend-icon="mdiEye"
				color="info"
				class="flex-grow-1"
				variant="tonal"
				@click="() => {
					showStorageDialog = false
					$router.push({ name: 'request-view'})
				}"
			>
				View In-Progress
			</v-btn>
			<v-btn
				color="secondary"
				class="flex-grow-1"
				variant="tonal"
				@click="showStorageDialog = false"
			>
				Back
			</v-btn>
		</v-card-actions>
	</v-card>
</v-dialog>
</template>

<styles lang="scss" scoped>
$spacing: 12px;

.app-bar {
    gap: $spacing;
    padding: 0 $spacing;
}
</styles>